import { getBurnRules, getProgram, isItemExcludedFromRedemption } from "./program";
import { getProviderProfile, providerPublishesProgram } from "./provider";
import type {
  ExcludableItem,
  LoyaltyDetails,
  LoyaltyPointsDiscount,
  LoyaltyProgram,
  ProgramDetailsEnvelope,
  RedemptionInput,
  RedemptionLimits,
  RedemptionPanelState,
} from "./types";

/**
 * What can be redeemed against the current bill, and what the panel should therefore show.
 *
 * Pure: the sale, the customer's balance and the program all arrive as arguments. This is the
 * layer the kiosk and mobile reuse verbatim — it is arithmetic and a state decision, with no
 * store, no dispatch and no platform API anywhere in it.
 */

/** Context every calculation here needs. */
export interface RedemptionContext {
  /** `metaId` of the provider in play. */
  providerMetaId?: string;
  /** The customer's balance with that provider, or `undefined` while it is still loading. */
  details?: LoyaltyDetails | null;
  /** The channel's loyalty program, if the provider has one. */
  program?: LoyaltyProgram | null;
  /** The current sale — only `revenue` and `saleItems` are read. */
  sale?: { revenue?: number; saleItems?: ExcludableItem[] | null } | null;
}

const NO_REDEMPTION: RedemptionInput = { redeemablePoints: 0, redeemableAmount: 0 };

/**
 * Money that `points` are worth.
 *
 * 🔴 **The single formula.** It is used for the on-screen preview AND for the `loyaltyInput` the
 * redeem/verify APIs are called with — previously those were two different expressions, so a
 * program-driven provider could show one figure and apply another (the preview multiplied
 * unfloored, and its `|| 1` fallback turned a genuinely-zero total into ₹1). Keep them one
 * function; a preview that can disagree with the payload is a customer-facing lie.
 *
 * Rate precedence, unchanged: the program's `redemptionValue` when it publishes one, otherwise
 * the ratio implied by the provider's own `redeemableAmount / redeemablePoints`. A provider
 * offering zero redeemable points implies a zero rate rather than a division by zero.
 */
export function pointsToAmount(
  points: number,
  { details, program, providerMetaId }: Pick<RedemptionContext, "details" | "program" | "providerMetaId">,
): RedemptionInput {
  if (!details) return NO_REDEMPTION;

  // Only a provider whose programme sets the rate reads it; everyone else takes the rate their
  // own numbers imply, even if a programme cached for this CHANNEL happens to publish one.
  let ratio = getProviderProfile(providerMetaId).usesProgramRedemptionValue
    ? program?.redemptionValue
    : undefined;
  if (ratio == null) {
    const redeemablePoints = details.redeemablePoints ?? 0;
    ratio = redeemablePoints > 0 ? (details.redeemableAmount ?? 0) / redeemablePoints : 0;
  }

  if (!points || points < 1) return NO_REDEMPTION;
  return { redeemablePoints: points, redeemableAmount: Math.floor(points * ratio) };
}

/**
 * The ceiling on this bill: how many points may be spent, and what they come to.
 *
 * ⚠️ **The two branches are not symmetric, and that is deliberate.** A provider that
 * `capsOwnRedemption` has already applied its caps (percentage, `burnCap`, minimum order value)
 * server side, so its numbers are taken as given. Every other partner is capped here instead:
 * against the sale's revenue, and against its own `percentageCapOnBillAmount` when it quotes one.
 *
 * 🔴 **On that branch the MONEY ceiling is derived first, and the POINTS ceiling from it**, at
 * the rate the provider's own quote implies (`redeemablePoints / redeemableAmount`). That order
 * is the point: capping the two independently let them describe different bills for any partner
 * not running at one point per unit of currency, and the POS panel pairs them in one sentence —
 * "Use 240 (₹120) loyalty Points". A percentage cap makes that divergence routine rather than
 * theoretical, which is why the conversion arrives with it.
 *
 * ✅ **Nothing here compares a point count against a currency figure any more.** The old
 * `Math.min(saleRevenue, …)` on the points ceiling did, and it was removed on 2026-09-10 — see
 * the note on `maxPoints` below for the arithmetic and for why it had to go WITH the conversion
 * rather than before it. `maxPoints` and `maxAmount` now describe the same bill by construction,
 * which is the property the panel's "Use 240 (₹120) loyalty Points" depends on.
 *
 * 🔴 **Both branches FLOOR the points ceiling, by the same rule.** The provider is the only
 * source of `redeemablePoints` and nothing upstream promises a whole number. Every reader takes
 * `maxPoints` literally — the POS and mobile panels print it, `applyMax` sends it as the
 * payload, and the partial editor seeds `String(...)` into a field whose `clampPointsInput`
 * strips the dot — so the fraction has to stop HERE rather than in four separate views. Money is
 * deliberately left alone.
 *
 * ⚠️ **`Math.floor`, never `Math.round`: this is a CEILING.** Rounding a maximum is the one mode
 * that can break it. A balance of 242.6 rounds to 243, and then every reader is over by four
 * tenths of a point that the customer does not have — the panel prints "Use 243", `applyMax`
 * sends 243 as the payload, and `canApplyPoints(243)` answers true, so the Apply button is live
 * for a figure the provider quoted no room for. Flooring can only ever under-serve, and by less
 * than one point. It also closes the sub-1 case for free: 0.6 floors to a ceiling of 0, which
 * `canApplyPoints` refuses and the kiosk card's `maxPoints < 1` guard hides outright, rather
 * than leaning on the `maxAmount < 1` guard beside it to catch a "Use 1" that should never have
 * been offered.
 *
 * ⚠️ This DEPARTS from `release_4.0.0`, which rounds. Release's `capsOwnRedemption` line reads
 * `Math.round(details?.redeemablePoints || 0)`, and its other branch rounds the rate conversion
 * before bounding it with `Math.min(..., redeemablePoints)`. Merged 2026-09-10 by flooring both:
 * a ceiling that rounds up quotes points the provider never offered. Release's balance clamp is
 * kept beside the floor as a float guard only — it cannot bind, and `maxPoints` below says why.
 *
 * `Math.floor` also matches `pointsToAmount`, which floors the money it derives, so a ceiling
 * and its value are never rounded in opposite directions.
 *
 * A sale with no `revenue` yields `NaN` from the `Math.min`, which the `|| 0` turns into zero —
 * i.e. nothing may be redeemed until the bill has a value. Also verbatim.
 */
export function getRedemptionLimits({
  providerMetaId,
  details,
  sale,
}: RedemptionContext): RedemptionLimits {
  const saleRevenue = Math.floor(sale?.revenue as number);

  if (getProviderProfile(providerMetaId).capsOwnRedemption) {
    return {
      // 🔴 FLOORED. A provider that caps its own redemption quotes the ceiling itself, and DotPe
      // — the only such partner — can quote a fractional one. Unfloored it reached every reader
      // raw: the POS panel printed "Use 242.4", `applyMax` redeemed 242.4, and the partial
      // editor was seeded with the string "242.4", whose dot `clampPointsInput` then strips —
      // turning an edit of that field into 2424 before the ceiling pulled it back. Down, never
      // to nearest: 242.6 must not become a ceiling of 243 the partner never offered.
      maxPoints: Math.floor(details?.redeemablePoints || 0),
      maxAmount: details?.redeemableAmount || 0,
    };
  }
  // The MONEY ceiling is derived first, because the points ceiling is derived FROM it.
  const redeemableAmount = details?.redeemableAmount ?? 0;
  const redeemablePoints = details?.redeemablePoints ?? 0;
  const capPercentage = details?.percentageCapOnBillAmount;
  const hasBillCap = Boolean(capPercentage);

  const maxAmount =
    (hasBillCap
      ? // 🔴 FLOORED, and it is the only money floored here. Everywhere else `maxAmount` is a
        // figure the PROVIDER quoted and its paise are the provider's; this one is arithmetic
        // OURS, and 33% of a ₹101 bill is ₹33.33. Unfloored it reached the headline as
        // "Use 33 (₹33.33) loyalty Points" while the payload — `pointsToAmount(33)`, which
        // floors the money IT derives — applied ₹33. A preview that can disagree with the
        // payload is the exact lie `pointsToAmount`'s header exists to forbid, so the two
        // derived-money sites floor by the same rule.
        Math.min(saleRevenue, Math.floor((saleRevenue * (capPercentage as number)) / 100), redeemableAmount)
      : Math.min(saleRevenue, redeemableAmount)) || 0;

  // Points per unit of currency, as THIS provider's own quote implies. A provider offering zero
  // of either implies no rate, and the conversion is skipped rather than dividing by zero.
  const pointsPerAmount = redeemableAmount && redeemablePoints ? redeemablePoints / redeemableAmount : 0;

  return {
    /**
     * 🔴 **The money ceiling, converted — and NOTHING compared against it in another unit.**
     *
     * `saleRevenue` used to be the first term of this `Math.min`. It is rupees, sitting in a
     * comparison whose other terms are points, and it was removed on 2026-09-10 because the
     * conversion above made it actively wrong rather than merely odd:
     *
     * | rate | bill | balance | with `saleRevenue` | correct |
     * |---|---|---|---|---|
     * | 1 pt/₹ | ₹1000 | 4000 pts (₹4000) | 1000 | 1000 |
     * | 2 pt/₹ | ₹1000 | 4000 pts (₹2000) | **1000** | **2000** |
     *
     * On the second row `maxAmount` is ₹1000 — the bill can be paid entirely in points — but
     * `maxPoints` came back 1000, which is worth ₹500. The panel headlined "Use 1000 (₹1000)"
     * and `applyMax` then sent 1000 points, so the customer was quoted a ₹1000 discount and
     * given ₹500. The two figures are printed in ONE sentence; they cannot describe two bills.
     *
     * It was a no-op at one point per unit of currency, which is every partner in production, so
     * nothing was observed to break. It was also load-bearing in the ORIGINAL function, where
     * `maxPoints` was capped independently of `maxAmount` — there the revenue term was the only
     * thing bounding points by the bill at all. Deriving points FROM the capped money is what
     * makes it redundant, so the two changes belong together and neither is safe alone.
     *
     * ⚠️ `Math.floor(redeemablePoints)` is kept, but it is a FLOAT GUARD, not a second cap —
     * correcting a claim this comment previously made. `maxAmount <= redeemableAmount` by
     * construction, so `maxAmount * (points / amount) <= points` algebraically and the clamp can
     * never bind. It survives only because that identity is fractional arithmetic: over 2M random
     * fixtures the product exceeded the balance by a float epsilon ~2.5% of the time (never by
     * enough to cross an integer, hence never binding after both floors). One `Math.min` term is
     * cheaper than reasoning about IEEE-754 at a ceiling that gets SENT somewhere.
     *
     * `NaN` from a valueless bill still reaches `maxAmount` as 0 above, so this lands on 0 too.
     */
    maxPoints: Math.min(Math.floor(maxAmount * pointsPerAmount), Math.floor(redeemablePoints)) || 0,
    maxAmount,
    billCapPercentage: hasBillCap ? (capPercentage as number) : undefined,
  };
}

/** The customer's whole balance with this provider — what "Total available" reports. */
export const getTotalPoints = (details?: LoyaltyDetails | null): number =>
  details?.currentPoints ?? 0;

/**
 * The points and money already on the bill.
 *
 * `discountRate` is stored negative (it is a deduction), so the amount is its magnitude.
 */
export function getAppliedRedemption(discount?: LoyaltyPointsDiscount | null) {
  return {
    appliedPoints: discount?.loyaltyPoints ?? 0,
    appliedAmount: Math.abs(discount?.discountRate ?? 0),
  };
}

/**
 * Normalise what a cashier typed into the points field.
 *
 * Returns a STRING so the field stays controlled and can be emptied. Non-digits are dropped and
 * anything above the ceiling is pulled down to it.
 *
 * 🔴 The comparison is NUMERIC. The old handler compared the raw input string against a number,
 * so JavaScript compared them lexicographically and `"9" > 100` was true — typing a 9 silently
 * snapped the field to the maximum.
 */
export function clampPointsInput(raw: string | number, maxPoints: number): string {
  const digits = String(raw ?? "").replace(/[^0-9]/g, "");
  if (!digits) return "";
  const points = parseInt(digits, 10);
  if (!Number.isFinite(points)) return "";
  return String(Math.min(points, Math.max(maxPoints, 0)));
}

/** The points field as a number. `""` and junk read as 0. */
export function parsePoints(raw: string | number): number {
  const points = parseInt(String(raw ?? ""), 10);
  return Number.isFinite(points) ? points : 0;
}

/** May this many points be applied? Zero, over-ceiling and worthless amounts are all refused. */
export function canApplyPoints(points: number, context: RedemptionContext): boolean {
  const { maxPoints } = getRedemptionLimits(context);
  if (!points || points < 1 || points > maxPoints) return false;
  return pointsToAmount(points, context).redeemableAmount > 0;
}

/**
 * Does THIS cart hold a line the programme bars from being paid for with points?
 *
 * 🔴 **The cart-line marker's predicate, over the cart's own lines.** It is
 * `isItemExcludedFromRedemption` across `sale.saleItems` — the list the POS and mobile carts render
 * unfiltered, one `ITEM_NOT_ELIGIBLE_FOR_REDEMPTION` check per line — so the ineligible card says
 * "some items are not eligible" exactly when at least one line on screen says so.
 *
 * ⚠️ Until 2026-09-17 the card asked whether the PROGRAMME excluded anything at all (v1's rule
 * too). A bill with nothing excluded on it was told "Some items in this cart are not eligible",
 * and the read was unpaired with the provider, so a programme cached for the channel could say it
 * under a partner that has no exclusions. Going through the cart-line predicate fixes both.
 */
export function cartHasExcludedItems({
  sale,
  program,
  providerMetaId,
}: Pick<RedemptionContext, "sale" | "program" | "providerMetaId">): boolean {
  return (sale?.saleItems ?? []).some((item) =>
    isItemExcludedFromRedemption(item, program, providerMetaId),
  );
}

/**
 * What the redemption panel should render — the whole decision, in one place.
 *
 * The states, in the order they are tested:
 *
 * | `kind` | when |
 * |---|---|
 * | `hidden` | no provider, or the balance has not arrived yet |
 * | `blocked` | the customer is blocked from loyalty usage |
 * | `applied` | a loyalty-points discount is on the bill |
 * | `ineligible` | the provider allows ₹0 against this bill |
 * | `available` | anything else |
 *
 * ⚠️ **`hidden` covers loading, which is new.** A missing balance used to fall through to
 * `available`, so between mount and the first response the panel offered "Use 0 (₹0)" with a
 * live Apply button. A failed fetch is not this case — the saga answers a zeroed balance, which
 * is present and lands in `ineligible`.
 */
export function getRedemptionPanelState(
  context: RedemptionContext & {
    hasActiveProvider: boolean;
    isBlocked?: boolean;
    appliedDiscount?: LoyaltyPointsDiscount | null;
    programDetails?: ProgramDetailsEnvelope | null;
  },
): RedemptionPanelState {
  const { hasActiveProvider, isBlocked, details, appliedDiscount, programDetails } = context;

  if (!hasActiveProvider || details == null) return { kind: "hidden" };

  const totalPoints = getTotalPoints(details);

  if (isBlocked) return { kind: "blocked", totalPoints };

  const limits = getRedemptionLimits(context);

  if (appliedDiscount) {
    const { appliedPoints, appliedAmount } = getAppliedRedemption(appliedDiscount);

    /**
     * 🔴 **The ceiling is taken against the bill this redemption is SITTING ON**, not against what
     * is left of it — `sale.revenue` is `billRoundedAmount` (rista-library `sale_util`), i.e. the
     * amount still payable, so the discount already taken off has to go back on first.
     *
     * Measured against the remainder it answers "how much MORE fits", which is not what the card
     * says or what the cashier can act on: the only control here is "Change amount", and that
     * needs the bill's whole capacity. It also made the figure MOVE on every change — Xeno, whose
     * 40% cap is re-applied to whatever is left, walked a ₹3696.67 bill down 1478 → 1278 → 1078 →
     * 887 as points went on — and, at the ceiling, print a sentence that contradicted its own
     * headline: "1478 Points (₹1478) applied … up to 887 Points can be used in this bill". A
     * partner quoting the plain balance (eazyrewards) collapsed to "up to 0" once the points
     * covered the bill, beside a balance of 8105.
     *
     * ⚠️ A provider that `capsOwnRedemption` never reads `sale`, so this is a NO-OP for DotPe —
     * its server-side quote still wins, and nothing here reconstructs it.
     */
    const billLimits = getRedemptionLimits({
      ...context,
      sale: { ...context.sale, revenue: (context.sale?.revenue ?? 0) + appliedAmount },
    });

    /**
     * Never quote a ceiling BELOW what is already on the bill.
     *
     * The grossed-up figure cannot be lower by construction, but two things reach past that: a
     * provider that re-quotes against the discounted bill itself (DotPe again — 40% of the
     * REMAINDER once a large redemption lands), and a cart edited after the points went on. Both
     * end in the same self-contradicting sentence, and this is the one line that closes it for
     * every provider at once.
     */
    const maxPoints = Math.max(billLimits.maxPoints, appliedPoints);

    return {
      kind: "applied",
      appliedPoints,
      appliedAmount,
      totalPoints,
      limits: { ...billLimits, maxPoints },
      /**
       * Offered whenever the bill can take points AT ALL — not only when something is left above
       * what is already applied.
       *
       * `changeAmount` releases the points and reopens the editor seeded with them, so the edit a
       * cashier wants at the ceiling is DOWNWARD. `appliedPoints < maxPoints` (v1's rule, and this
       * file's until 2026-09-18) hid the control at exactly that point: 1477 applied showed it,
       * 1478 did not, and the only way down was Remove → Use partial → retype, with the bill
       * carrying no discount in between.
       *
       * 🔴 **The UNFLOORED capacity, and not simply `true`.** The release happens first, so on a
       * bill that can take nothing — a provider quoting zero after a failed refund, a cart edited
       * down past the redemption — reopening the editor would strand the cashier with the discount
       * gone and nothing able to replace it. There, Remove is the only sane control and stays the
       * only one. `maxPoints` cannot express that: it is floored at `appliedPoints` just above, so
       * it is positive whenever anything is applied.
       */
      canChangeAmount: billLimits.maxPoints > 0,
    };
  }

  if (details.redeemableAmount === 0) {
    // Paired: an envelope handed in can be any partner's cached programme, and only a publishing
    // provider's minimum order value (or exclusions) describe this bill.
    const program = providerPublishesProgram(context.providerMetaId) ? getProgram(programDetails) : null;
    return {
      kind: "ineligible",
      totalPoints,
      minOrderValue: getBurnRules(program).minOrderValue,
      cartHasExcludedItems: cartHasExcludedItems({ ...context, program }),
    };
  }

  return { kind: "available", totalPoints, limits };
}

/**
 * What a redeem response MEANS. Pure, so the decision can be tested without a renderer.
 *
 * 🔴 **`failed` is not only an error code.** The saga answers a request failure with
 * `undefined`, not with a code: `commonPostData` swallows every error and returns nothing, so
 * `updateRedeemPointsData` falls to its empty-response branch and invokes the caller's callback
 * with `undefined`. A classifier that asked only about `code`/`errorCode` would call that a
 * SUCCESS and hand the caller a sale it does not have.
 *
 * That is exactly what the panel used to do — it read `res.sale` straight after the code check
 * and threw a `TypeError` on every failed redemption. The throw travelled back into the saga's
 * own `try`, and its `catch` swallowed it: at the time that catch read
 * `actions.payload?.data?.callback?.()`, a path with no `data` key, so a silent no-op. (The
 * `.data.` typo was fixed on 2026-09-08, so the catch would now call back — but only if it were
 * reachable, which `commonPostData` still makes it not.) Nothing was logged, nothing was shown,
 * and the customer saw a panel that simply did not redeem. Hence the `!res?.sale` arm: no sale
 * means no redemption, whatever the response looked like.
 *
 * `challenge` is a SUCCESS: the points are already on the returned sale and the provider is
 * additionally asking for an OTP. Callers must save the sale in both non-failed cases —
 * `useKioskPointsRedemption`'s `otp.cancel` depends on the discount actually being there.
 */
export type RedeemOutcome =
  | { kind: "applied"; sale: any }
  | { kind: "challenge"; sale: any }
  | { kind: "failed"; code?: string };

export function classifyRedeemResponse(res: any): RedeemOutcome {
  const code = res?.code || res?.errorCode;
  if (code) return { kind: "failed", code };
  if (!res?.sale) return { kind: "failed" };
  return res?.redemptionInfo?.authenticate
    ? { kind: "challenge", sale: res.sale }
    : { kind: "applied", sale: res.sale };
}

/* ── keeping a CHECKOUT in step with the points on the bill ──────────────────────────── */

/**
 * A fingerprint of what the redeemed points are currently doing to this bill.
 *
 * Applying, removing or changing points mutates the sale from INSIDE the checkout, and a
 * checkout screen's payment state is a snapshot taken when it opened: the balance it will charge,
 * the payment legs, the cash strip. Nothing in that snapshot re-derives itself, so without a
 * signal the screen goes on quoting the pre-points total — and it is the customer's money.
 *
 * All three figures are needed, and none is redundant:
 *
 * | | why |
 * |---|---|
 * | `loyaltyPoints` | a partial edit can change the points at the same rate, leaving the money identical |
 * | `discountRate` | the same points can be worth a different amount after a programme change |
 * | `billRoundedAmount` | what the screen actually charges; it moves for reasons the discount alone does not show |
 *
 * ⚠️ **A number, not a reference.** `saveSaleData` replaces the sale object on every write, so
 * watching `billDiscountList` or the discount itself would fire on every unrelated cart edit and
 * re-prep the checkout underneath the cashier.
 */
export function buildLoyaltySyncSignature(
  discount: LoyaltyPointsDiscount | null | undefined,
  billRoundedAmount?: number,
): string {
  return `${discount?.loyaltyPoints ?? 0}|${discount?.discountRate ?? 0}|${billRoundedAmount ?? 0}`;
}

/**
 * What a checkout should do about the signature it is now seeing.
 *
 * - `"none"` — the checkout is closed. The remembered signature is DROPPED, which is the
 *   load-bearing part: without it a close-and-reopen on an unchanged bill would look like a
 *   points change and re-prep a screen that had just prepped itself.
 * - `"sync"` — first pass with the checkout open. Prepare the payment snapshot, exactly as the
 *   mount-time prep always did.
 * - `"resync"` — the signature moved while the checkout stayed open, i.e. points were applied,
 *   removed or changed in place. Prepare again, and additionally re-seed whatever the platform's
 *   payment layer derives from the balance.
 *
 * Pure so the distinction is testable: `"sync"` and `"resync"` differ only in that second step,
 * and getting it backwards means either a screen that never re-seeds or one that re-seeds itself
 * every time it opens.
 */
export type LoyaltySyncAction = "none" | "sync" | "resync";

export function nextLoyaltySyncState(
  prevSignature: string | null,
  current: { isOpen: boolean; signature: string },
): { prevSignature: string | null; action: LoyaltySyncAction } {
  if (!current.isOpen) return { prevSignature: null, action: "none" };
  const isResync = prevSignature !== null && prevSignature !== current.signature;
  return { prevSignature: current.signature, action: isResync ? "resync" : "sync" };
}
